import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Mail, ArrowRight, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'
import { API_CONFIG } from '../../../config/api-keys.js'
import Button from '../components/common/Button'

const supabase = createClient(
  API_CONFIG.supabase.url,
  API_CONFIG.supabase.anonKey
)

export default function ConfirmEmail() {
  const location = useLocation()
  const navigate = useNavigate()
  const email = location.state?.email || 'your email'
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [error, setError] = useState('')

  const handleResendConfirmation = async () => {
    if (email === 'your email') {
      setError('Email address not found. Please sign up again.')
      return
    }

    setResending(true)
    setError('')

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`
        }
      })

      if (error) throw error

      setResent(true)
      setTimeout(() => setResent(false), 5000)
    } catch (err) {
      console.error('[Confirm Email] Resend error:', err)
      setError(err.message || 'Failed to resend confirmation email. Please try again.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Helmet>
        <title>Confirm Your Email | HireFlow</title>
        <meta name="description" content="Confirm your email to get started with HireFlow" />
      </Helmet>

      <div className="container mx-auto px-4 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* Icon */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6"
            >
              <Mail className="w-10 h-10 text-white" />
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Check Your Email
            </h1>

            <p className="text-xl text-neutral-600 mb-2">
              We've sent a confirmation email to:
            </p>

            <p className="text-2xl font-semibold text-blue-600 mb-8">
              {email}
            </p>
          </div>

          {/* Instructions Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8 mb-6"
          >
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">
              What's Next?
            </h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">
                    Check your inbox
                  </h3>
                  <p className="text-neutral-600">
                    Look for an email from <strong>HireFlow</strong> with the subject "Confirm your email address"
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">
                    Click the confirmation link
                  </h3>
                  <p className="text-neutral-600">
                    Open the email and click the "Confirm your email" button to activate your account
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900 mb-1">
                    Start using HireFlow
                  </h3>
                  <p className="text-neutral-600">
                    Once confirmed, you'll be redirected to start creating your professional resumes!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Resend email section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-neutral-50 rounded-xl p-6 mb-6"
          >
            <h3 className="font-semibold text-neutral-900 mb-2">
              Didn't receive the email?
            </h3>
            <p className="text-neutral-600 mb-4">
              Check your spam folder, or we can send you another confirmation email.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {resent && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                ✓ Confirmation email sent! Check your inbox.
              </div>
            )}

            <button
              onClick={handleResendConfirmation}
              disabled={resending || resent}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-200 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 hover:border-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-5 h-5 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Sending...' : resent ? 'Email Sent!' : 'Resend Confirmation Email'}
            </button>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              variant="secondary"
              onClick={() => navigate('/pricing')}
            >
              Back to Pricing
            </Button>

            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              className="flex items-center gap-2"
            >
              Go to Login
              <ArrowRight className="w-5 h-5" />
            </Button>
          </motion.div>

          {/* Help text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-8"
          >
            <p className="text-neutral-500 text-sm">
              Need help?{' '}
              <a
                href="/contact"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                Contact Support
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
